import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type Order = {
  id_zakazky: string;
  product_code: string;
  product_name: string;
  norm_sec: number;
  remaining_sec: number;
  progress: number;
};
type Worker = { name: string; remaining_sec: number; orders: Order[] };

/** Švadlenka, které zbývá míň, bude brzy potřebovat další zakázku — zvýrazníme. */
const LOW_WORK_SEC = 60 * 60;

function fmtDuration(sec: number): string {
  const total = Math.max(0, Math.round(sec / 60));
  return `${Math.floor(total / 60)} hod ${total % 60} min`;
}

/**
 * Timeline — kdo právě šije, jak daleko je a kolik mu zbývá. Slouží střihárně
 * k plánování: nahoře je vidět, komu docházi práce, dole rozpracované zakázky.
 */
export function TimelinePage() {
  const [workers, setWorkers] = useState<Worker[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ workers: Worker[] }>('/api/vyroba/timeline')
      .then((d) => setWorkers(d.workers))
      .catch((e: unknown) => {
        setWorkers([]);
        setError(e instanceof ApiError ? e.message : 'Chyba načítání');
      });
  }, []);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Timeline</h1>
          <p className="text-sm text-text-secondary mt-0.5">
            Kdo právě šije, jak je daleko a kolik mu zbývá do konce zakázky.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/vyroba/striharna"
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors shrink-0"
          >
            Plán stříhání
          </Link>
          <Link
            to="/vyroba/dokoncovaci-dilna"
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors shrink-0"
          >
            Dokončovací dílna
          </Link>
        </div>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {workers === null ? (
        <p className="text-sm text-text-muted">Načítám…</p>
      ) : workers.length === 0 ? (
        <p className="text-sm text-text-muted">
          Žádné švadlenky. Nejdřív je nasynchronizuj v Zdroje → Databáze švadlenek.
        </p>
      ) : (
        <>
          {/* Přehled: kolik komu zbývá práce */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {workers.map((w) => (
              <div key={w.name} className="bg-primary-25 border border-primary-100 rounded-xl p-4">
                <div className="text-center font-bold text-text">{w.name}</div>
                <div className="text-center text-sm text-text-muted mt-1">Zbývá</div>
                <div
                  className={`text-center text-xl font-bold mt-1 ${
                    w.remaining_sec < LOW_WORK_SEC ? 'text-danger' : 'text-text'
                  }`}
                >
                  {fmtDuration(w.remaining_sec)}
                </div>
              </div>
            ))}
          </div>

          {/* Rozpracované zakázky po švadlenkách */}
          <div className="space-y-6">
            {workers.map((w) => (
              <div key={w.name}>
                <h2 className="text-base font-bold text-text mb-2">{w.name}</h2>
                {w.orders.length === 0 ? (
                  <p className="text-sm text-text-muted">Nemá rozpracovanou zakázku.</p>
                ) : (
                  <div className="space-y-3">
                    {w.orders.map((o) => (
                      <div
                        key={o.id_zakazky}
                        className="flex items-center gap-4 flex-wrap bg-surface rounded-xl shadow-card px-4 py-3"
                      >
                        <div className="w-56 shrink-0">
                          <div className="h-6 rounded border border-action/40 bg-white overflow-hidden">
                            <div
                              className="h-full bg-action flex items-center justify-center text-white text-xs font-bold transition-all"
                              style={{ width: `${Math.max(o.progress, 8)}%` }}
                            >
                              {o.progress} %
                            </div>
                          </div>
                        </div>
                        <div
                          className={`w-32 shrink-0 text-sm font-medium ${
                            o.remaining_sec < LOW_WORK_SEC ? 'text-danger' : 'text-text'
                          }`}
                        >
                          {fmtDuration(o.remaining_sec)}
                        </div>
                        <div className="w-20 shrink-0 text-sm font-bold text-text">
                          {o.id_zakazky}
                        </div>
                        <div className="min-w-0 flex-1 text-sm text-text-secondary truncate">
                          {o.product_code && (
                            <strong className="text-text">{o.product_code}</strong>
                          )}
                          {o.product_code && ' – '}
                          {o.product_name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
